import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import {
    createFormTemplateGroup,
    updateFormTemplateGroup,
} from '../../graphql/resourceLibraryQueries';
import { CustomModal } from '../CustomModal';
import { Btn } from '../ui';
import '../../css/ResourceLibraryContent.css';

interface FormTemplateGroupItem {
    id: string;
    name: string;
    description?: string | null;
    resourceLibraryID?: string | null;
}

interface CreateEditGroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    editingGroup: FormTemplateGroupItem | null;
}

export const CreateEditGroupModal: React.FC<CreateEditGroupModalProps> = ({
    isOpen,
    onClose,
    onSave,
    editingGroup,
}) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isEditing = !!editingGroup;

    // Pre-populate when editing
    useEffect(() => {
        if (isOpen && editingGroup) {
            setName(editingGroup.name || '');
            setDescription(editingGroup.description || '');
        } else if (isOpen) {
            setName('');
            setDescription('');
        }
        setError(null);
    }, [isOpen, editingGroup]);

    const handleSubmit = async () => {
        if (!name.trim()) {
            setError('Template Group Name is required.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            if (isEditing && editingGroup) {
                await updateFormTemplateGroup({
                    id: editingGroup.id,
                    name: name.trim(),
                    description: description.trim() || undefined,
                });
            } else {
                await createFormTemplateGroup({
                    name: name.trim(),
                    description: description.trim() || undefined,
                });
            }
            onSave();
        } catch (err) {
            console.error('Error saving group:', err);
            Sentry.captureException(err);
            setError('Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <CustomModal
            visible={isOpen}
            onDismiss={onClose}
            header={isEditing ? 'Edit Template Group' : 'Create Template Group'}
            bodyClassName="zrl-dialog-body"
            footer={
                <div className="modal-footer-actions">
                    <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} />
                    <Btn
                        variant="primary"
                        buttonText={saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Group'}
                        buttonDisabled={saving || !name.trim()}
                        buttonOnClick={handleSubmit}
                    />
                </div>
            }
        >
                    <div className="zrl-form-group">
                        <label className="zrl-form-label">Template Group Name *</label>
                        <input
                            className="zrl-input"
                            type="text"
                            placeholder="e.g., MSHA Inspection Forms"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>

                    <div className="zrl-form-group">
                        <label className="zrl-form-label">Description</label>
                        <textarea
                            className="zrl-textarea"
                            placeholder="Optional description for this template group..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                        />
                    </div>

                    {error && (
                        <div className="zrl-warning-banner" style={{ marginTop: 8 }}>
                            <span className="zrl-warning-icon">⚠️</span>
                            <span>{error}</span>
                        </div>
                    )}
        </CustomModal>
    );
};
